import { formatDate } from '@clim/client-core';
import { useEffect, useState } from 'react';
import { ErrorBox, Modal } from '../../components/ui';
import { downloadFile } from '../../lib/download';
import { errorMessage } from '../../lib/errors';
import { useEngine, useReady } from '../../lib/session';

interface ClientPrivacy {
  opposedAt: string | null;
  opposition: string | null;
}

/**
 * Fiche client (administrateur) : opposition aux sollicitations au cas par cas, et export des données. Aucune
 * suppression : les demandes d'effacement sont traitées en interne, manuellement.
 */
export function ClientPrivacyCard({ clientId, name }: { clientId: string; name: string }) {
  const { api } = useReady();
  const engine = useEngine();
  const [s, setS] = useState<ClientPrivacy | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = () =>
    void api
      .request<ClientPrivacy>('GET', `/v1/privacy/clients/${clientId}`)
      .then(setS, (e) => setError(errorMessage(e)));
  useEffect(load, [api, clientId]);
  const exportData = async () => {
    setError(null);
    try {
      const data = await api.request('GET', `/v1/privacy/clients/${clientId}/export`);
      downloadFile(
        JSON.stringify(data, null, 2),
        `donnees-${name
          .toLowerCase()
          .normalize('NFD')
          .replace(/[^a-z0-9]+/g, '-')}.json`,
        'application/json',
      );
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const toggle = async () => {
    setError(null);
    try {
      setS(
        await api.request<ClientPrivacy>(
          s?.opposedAt ? 'DELETE' : 'POST',
          `/v1/privacy/clients/${clientId}/opposition`,
        ),
      );
      setConfirm(false);
      void engine.sync();
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  if (!s) return <ErrorBox message={error} />;
  return (
    <div className="card">
      <h2>Données personnelles</h2>
      {s.opposedAt ? (
        <p className="warning-box">
          Opposition aux sollicitations depuis le {formatDate(s.opposedAt)} : aucune demande d’avis.
        </p>
      ) : (
        <p className="muted">Aucune opposition : le client peut recevoir les demandes d’avis.</p>
      )}
      <div className="actions">
        <button className="btn small" onClick={() => void exportData()}>
          Exporter ses données
        </button>
        <button className="btn small" onClick={() => (s.opposedAt ? void toggle() : setConfirm(true))}>
          {s.opposedAt ? 'Lever l’opposition' : 'Opposition aux sollicitations'}
        </button>
      </div>
      <ErrorBox message={error} />
      {confirm && (
        <Modal title="Opposition aux sollicitations" onClose={() => setConfirm(false)}>
          <p>
            {name} ne recevra plus de demande d’avis. Le suivi de ses dossiers (SAV, garantie) continue. Vous
            pourrez lever l’opposition à tout moment.
          </p>
          <div className="actions">
            <button className="btn" onClick={() => setConfirm(false)}>
              Annuler
            </button>
            <button className="btn primary" onClick={() => void toggle()}>
              Confirmer
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
